import React, { useEffect, useRef, useState } from "react";
import {
  Dimensions,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { v2Styles as styles } from "./LocationSummaryV2.styles";
import { COLUMN_SECTIONS } from "./LocationSummaryV2Table.shared";

const MOBILE_BREAKPOINT = 640;

interface Props {
  isOpen: boolean;
  anchorRef: React.RefObject<View | null>;
  visible: Record<string, boolean>;
  onToggleColumn: (columnKey: string) => void;
  onRequestClose: () => void;
}

type ContainsTarget = {
  contains?: (target: Node) => boolean;
} | null;

const LocationSummaryColumnsDropdown: React.FC<Props> = ({
  isOpen,
  anchorRef,
  visible,
  onToggleColumn,
  onRequestClose,
}) => {
  const popoverRef = useRef<View | null>(null);

  // ── Screen-space position for the dropdown, always rendered via Modal ────────
  // A plain absolutely-positioned View was tried previously, but the page wraps
  // the table + companion table in one scrolling ScrollView; that ancestor forms
  // its own stacking context, so no zIndex value on the dropdown can reliably
  // out-rank the companion table sibling painted after it. Routing through a
  // Modal (portal) sidesteps stacking entirely — the same fix already used below
  // MOBILE_BREAKPOINT is now applied unconditionally.
  const [menuLayout, setMenuLayout] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);
  const { width: screenWidth, height: screenHeight } = Dimensions.get("window");
  const isMobile = Platform.OS !== "web" || screenWidth < MOBILE_BREAKPOINT;

  // Measure the Columns button whenever the dropdown opens
  useEffect(() => {
    if (!isOpen) return;
    const anchor = anchorRef.current;
    if (!anchor) return;

    anchor.measureInWindow((x, y, width, height) => {
      const dropdownWidth = isMobile ? Math.min(320, screenWidth - 32) : 240;
      const left = isMobile
        ? Math.max(
            16,
            Math.min(
              x + width - dropdownWidth,       // right-align with button
              screenWidth - dropdownWidth - 16, // clamp to right viewport edge
            ),
          )
        : Math.max(16, Math.min(x, screenWidth - dropdownWidth - 16)); // left-align with button
      const top = y + height + (isMobile ? 8 : 4);
      const maxHeight = Math.max(200, Math.min(480, screenHeight - top - 24));
      setMenuLayout({ top, left, width: dropdownWidth, maxHeight });
    });
  }, [isOpen, isMobile, anchorRef, screenWidth, screenHeight]);

  // ── Web: outside-click / Escape handlers ─────────────────────────────────────
  useEffect(() => {
    if (!isOpen || typeof document === "undefined") return;

    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      const anchorNode = anchorRef.current as unknown as ContainsTarget;
      const popoverNode = popoverRef.current as unknown as ContainsTarget;
      if (anchorNode?.contains?.(target) || popoverNode?.contains?.(target)) return;
      onRequestClose();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onRequestClose();
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [anchorRef, isOpen, onRequestClose]);

  if (!isOpen) return null;

  // ── Shared dropdown content ───────────────────────────────────────────────────
  const dropdownContent = (
    <ScrollView
      style={[styles.columnMenuScroll, menuLayout != null && { maxHeight: menuLayout.maxHeight }]}
      keyboardShouldPersistTaps="handled"
    >
      {COLUMN_SECTIONS.map((section, sectionIndex) => (
        <View
          key={section.label}
          style={[
            styles.columnSection,
            sectionIndex > 0 && styles.columnSectionDivider,
          ]}
        >
          <Text style={styles.columnSectionTitle}>{section.label}</Text>
          {section.options.map((option) => {
            const isChecked = visible[option.key] !== false;
            return (
              <TouchableOpacity
                key={option.key}
                style={styles.columnOption}
                onPress={() => onToggleColumn(option.key)}
              >
                <MaterialIcons
                  name={isChecked ? "check-box" : "check-box-outline-blank"}
                  size={18}
                  color={isChecked ? "#005CBC" : "#9AA1AB"}
                />
                <Text style={styles.columnOptionText}>{option.label}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      ))}
    </ScrollView>
  );

  // ── Always render via Modal so the dropdown escapes the page's clipping/stacking ScrollView ──
  // Keep the dropdown invisible until we have coordinates (avoids a flash at 0,0)
  const opacity = menuLayout ? 1 : 0;
  const posStyle = menuLayout
    ? { top: menuLayout.top, left: menuLayout.left, width: menuLayout.width }
    : { top: 0, left: 0, width: 240 };

  return (
    <Modal
      visible
      transparent
      animationType="none"
      onRequestClose={onRequestClose}
      testID="asn-v2-column-menu-modal"
    >
      {/* Full-screen backdrop — tap outside to close */}
      <Pressable
        style={StyleSheet.absoluteFillObject}
        onPress={onRequestClose}
        accessible={false}
      />
      <View
        ref={popoverRef}
        style={[styles.columnMenu, portalMenuStyles.menu, posStyle, { opacity }]}
        testID="asn-v2-column-menu-popover"
      >
        {dropdownContent}
      </View>
    </Modal>
  );
};

// Screen-space overrides — the menu is positioned inside a Modal via measureInWindow,
// so replace the relative-positioning defaults (left:0, top:40) from styles.columnMenu.
const portalMenuStyles = StyleSheet.create({
  menu: {
    position: "absolute",
  },
});

export default LocationSummaryColumnsDropdown;
